import {
  HeavyShippingStatus,
  StandardShippingStatus,
} from "@/app/ec/product/[basePath]/vendor-options/configureVendorOptions";

/**
 * What the shopper pays to ship the chosen vendor's offer, in the terms the pricing area shows. The
 * vendor's free-shipping threshold isn't part of the vendor option, so it is passed in; null or 0 means none,
 * unless neo's free-shipping gap shows there is one.
 */
export default function getShippingSummary(
  vendorOption: VendorOptionState,
  freeShippingThreshold: number | null,
): ShippingSummary {
  const threshold = getThreshold(vendorOption, freeShippingThreshold);
  const { heavyShippingStatus, heavyShipping, freeShippingGap } = vendorOption;

  return {
    standard: getStandardShipping(vendorOption, threshold),
    heavyShipping:
      heavyShippingStatus === HeavyShippingStatus.HAS_KNOWN_HEAVY_SHIPPING && heavyShipping > 0 ? heavyShipping : null,
    heavyShippingUnknown: heavyShippingStatus === HeavyShippingStatus.HAS_UNKNOWN_HEAVY_SHIPPING,
    spendForFreeShipping: freeShippingGap > 0 ? freeShippingGap : null,
  };
}

// Neo only reports a gap when the vendor has a threshold, and for standard shipping the gap is the threshold
// less this item's subtotal, so a failed threshold lookup can be filled in from it (rounded to cents).
function getThreshold(
  { standardShippingStatus, freeShippingGap, productSubTotal }: VendorOptionState,
  freeShippingThreshold: number | null,
): number | null {
  if (freeShippingThreshold !== null && freeShippingThreshold > 0) {
    return freeShippingThreshold;
  }
  if (standardShippingStatus === StandardShippingStatus.STANDARD_SHIPPING && freeShippingGap > 0) {
    return Math.round((productSubTotal + freeShippingGap) * 100) / 100;
  }
  return null;
}

function getStandardShipping(
  { standardShippingStatus, standardShipping, productSubTotal }: VendorOptionState,
  threshold: number | null,
): StandardShipping {
  switch (standardShippingStatus) {
    case StandardShippingStatus.NO_ADDITIONAL_SHIPPING:
      return { kind: "included" };
    case StandardShippingStatus.FREE_SHIPPING:
      // A vendor without a shipping fee ships free whatever the order; otherwise the threshold was met,
      // by this item alone or together with what is already in the cart.
      if (!standardShipping || threshold === null) {
        return { kind: "free", reason: "always", threshold };
      }
      return { kind: "free", reason: productSubTotal >= threshold ? "item" : "cart", threshold };
    default:
      return { kind: "cost", cost: standardShipping, threshold };
  }
}

/** Standard shipping plus any known heavy-shipping fee. */
export function getTotalShipping({ standard, heavyShipping }: ShippingSummary): number {
  return (standard.kind === "cost" ? standard.cost : 0) + (heavyShipping ?? 0);
}

export type StandardShipping =
  /** "+ $4.99 Standard Shipping" */
  | { kind: "cost"; cost: number; threshold: number | null }
  /** "& FREE Standard Shipping" */
  | { kind: "free"; reason: "always" | "item" | "cart"; threshold: number | null }
  /** "& FREE Shipping Included": an item already in the cart from this vendor covers it. */
  | { kind: "included" };

export interface ShippingSummary {
  standard: StandardShipping;
  /** Heavy-shipping fee for the quantity, or null when there is none. */
  heavyShipping: number | null;
  /** A heavy-shipping fee applies, but Net32 only learns it after the order is placed. */
  heavyShippingUnknown: boolean;
  /** How much more to spend with the vendor to ship free, or null. */
  spendForFreeShipping: number | null;
}
